import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  MessageSquare, Plus, Trash2, Sparkles, Loader2, Send,
  Download, ThumbsUp, ThumbsDown, Minus, Eye, User, ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sourceConfig = {
  auto_template: { label: "Auto", bg: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  auto_ai: { label: "AI", bg: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  wordpress: { label: "WordPress", bg: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  facebook: { label: "Facebook", bg: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400" },
  twitter: { label: "Twitter", bg: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400" },
  instagram: { label: "Instagram", bg: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400" },
  manual: { label: "Reczny", bg: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
};

const sentimentIcon = {
  positive: { icon: ThumbsUp, color: "text-emerald-500" },
  negative: { icon: ThumbsDown, color: "text-red-500" },
  neutral: { icon: Minus, color: "text-slate-400" },
};

export default function Comments() {
  const { t } = useLanguage();
  const [comments, setComments] = useState([]);
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedComment, setSelectedComment] = useState(null);
  const [replyingId, setReplyingId] = useState(null);
  const [sentimentId, setSentimentId] = useState(null);
  const [importingSite, setImportingSite] = useState(null);
  const [replyTone, setReplyTone] = useState("friendly");
  const [filterSource, setFilterSource] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ author: "", content: "", source: "manual", post_title: "" });

  const fetchData = useCallback(async () => {
    try {
      const params = filterSource !== "all" ? `?source=${filterSource}` : "";
      const [commRes, statsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/comments${params}`),
        axios.get(`${API}/comments/stats`),
        axios.get(`${API}/sites`),
      ]);
      setComments(commRes.data);
      setStats(statsRes.data);
      setSites(sitesRes.data.filter(s => s.site_type === "wordpress"));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterSource]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    try {
      await axios.post(`${API}/comments`, form);
      toast.success(t("common.success"));
      fetchData(); setDialogOpen(false);
      setForm({ author: "", content: "", source: "manual", post_title: "" });
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleImport = async (siteId) => {
    setImportingSite(siteId);
    try {
      const res = await axios.post(`${API}/comments/import-wp`, { site_id: siteId });
      toast.success(`${t("comments.imported")}: ${res.data.imported}`);
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
    finally { setImportingSite(null); }
  };

  const handleReply = async (commentId) => {
    setReplyingId(commentId);
    try {
      const res = await axios.post(`${API}/comments/${commentId}/reply`, { ai_model: "gemini", tone: replyTone });
      toast.success(t("comments.replied"));
      fetchData();
      if (selectedComment?.id === commentId) setSelectedComment(c => ({ ...c, reply: res.data.reply, status: "replied" }));
    } catch (err) { toast.error(t("common.error")); }
    finally { setReplyingId(null); }
  };

  const handleSentiment = async (commentId) => {
    setSentimentId(commentId);
    try {
      const res = await axios.post(`${API}/comments/${commentId}/sentiment`);
      fetchData();
      if (selectedComment?.id === commentId) setSelectedComment(c => ({ ...c, sentiment: res.data }));
    } catch (err) { toast.error(t("common.error")); }
    finally { setSentimentId(null); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/comments/${id}`);
      if (selectedComment?.id === id) setSelectedComment(null);
      toast.success(t("common.success")); fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  return (
    <div data-testid="comments-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("comments.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("comments.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          {/* Import z WP */}
          {sites.length > 0 && (
            <Select value="none" onValueChange={(v) => { if (v !== "none") handleImport(v); }}>
              <SelectTrigger data-testid="import-comments-btn" className="h-9 text-xs w-[160px] gap-1">
                <Download className="w-3 h-3" strokeWidth={1.5} />
                <SelectValue placeholder={t("comments.importWp")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-xs">{t("comments.importWp")}</SelectItem>
                {sites.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-comment-btn" variant="outline" className="rounded-lg gap-2">
                <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("comments.addManual")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">{t("comments.addManual")}</DialogTitle></DialogHeader>
              <div className="grid gap-3 py-4">
                <Input data-testid="comment-author" placeholder={t("comments.author")} value={form.author} onChange={(e) => setForm({...form, author: e.target.value})} />
                <Textarea data-testid="comment-content" placeholder={t("comments.content")} value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} rows={3} />
                <Input placeholder={t("comments.postTitle")} value={form.post_title} onChange={(e) => setForm({...form, post_title: e.target.value})} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("sites.cancel")}</Button>
                <Button data-testid="save-comment-btn" onClick={handleAdd}>{t("sites.save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats + filter */}
      <div className="flex items-center gap-3 flex-wrap">
        {stats && (
          <>
            <Badge variant="secondary" className="text-xs rounded-md">{stats.total} {t("comments.total")}</Badge>
            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs border-0 rounded-md">{stats.new} {t("comments.new")}</Badge>
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs border-0 rounded-md">{stats.replied} {t("comments.repliedCount")}</Badge>
          </>
        )}
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="h-7 text-[10px] w-[120px] ml-auto"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">{t("comments.allSources")}</SelectItem>
            {Object.entries(sourceConfig).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista */}
        <div className={selectedComment ? "lg:col-span-1" : "lg:col-span-3"}>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-0">
              {comments.length === 0 ? (
                <div data-testid="no-comments" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <MessageSquare className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">{t("comments.noComments")}</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[500px]">
                  <div className="divide-y divide-border">
                    {comments.map((c) => {
                      const src = sourceConfig[c.source] || sourceConfig.manual;
                      const sent = c.sentiment ? sentimentIcon[c.sentiment.sentiment] : null;
                      const SentIcon = sent?.icon || Minus;
                      return (
                        <button key={c.id} data-testid={`comment-${c.id}`} onClick={() => setSelectedComment(c)}
                          className={`w-full flex items-start gap-3 p-3 text-left transition-all hover:bg-accent/30 ${selectedComment?.id === c.id ? "bg-primary/5" : ""}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-semibold text-foreground">{c.author || "Anonim"}</span>
                              <Badge className={`text-[8px] border-0 rounded-md ${src.bg}`}>{src.label}</Badge>
                              {c.status === "replied" && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[8px] border-0 rounded-md">OK</Badge>}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{c.content.substring(0, 80)}</p>
                            {c.source_url && <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline truncate block mt-0.5" onClick={e => e.stopPropagation()}><ExternalLink className="w-2.5 h-2.5 inline mr-0.5" />{c.source_url.substring(0, 50)}</a>}
                          </div>
                          {sent && <SentIcon className={`w-3.5 h-3.5 shrink-0 ${sent.color}`} strokeWidth={1.5} />}
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail */}
        {selectedComment && (
          <div className="lg:col-span-2">
            <Card data-testid="comment-detail" className="rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2 pt-4 px-5">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-sm font-heading flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                      {selectedComment.author || "Anonim"}
                    </CardTitle>
                    {selectedComment.post_title && <p className="text-[10px] text-muted-foreground mt-0.5">Re: {selectedComment.post_title}</p>}
                    {selectedComment.source_url && <a href={selectedComment.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline flex items-center gap-1 mt-0.5"><ExternalLink className="w-3 h-3" /> Otworz artykul</a>}
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDelete(selectedComment.id)}>
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5 pt-2 space-y-3">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-foreground/80 leading-relaxed">{selectedComment.content}</p>
                </div>

                {/* Sentiment */}
                <div className="flex items-center gap-2">
                  {selectedComment.sentiment ? (
                    <Badge className={`text-[10px] border-0 rounded-md gap-1 ${
                      selectedComment.sentiment.sentiment === "positive" ? "bg-emerald-100 text-emerald-700" :
                      selectedComment.sentiment.sentiment === "negative" ? "bg-red-100 text-red-600" :
                      "bg-slate-100 text-slate-500"
                    }`}>
                      {selectedComment.sentiment.sentiment} ({selectedComment.sentiment.score})
                    </Badge>
                  ) : (
                    <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1"
                      onClick={() => handleSentiment(selectedComment.id)} disabled={sentimentId === selectedComment.id}>
                      {sentimentId === selectedComment.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Eye className="w-2.5 h-2.5" />}
                      {t("comments.analyzeSentiment")}
                    </Button>
                  )}
                </div>

                {/* Reply */}
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-foreground">{t("comments.aiReply")}</p>
                    <div className="flex items-center gap-2">
                      <Select value={replyTone} onValueChange={setReplyTone}>
                        <SelectTrigger className="h-6 text-[10px] w-[100px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="friendly" className="text-xs">{t("comments.toneFriendly")}</SelectItem>
                          <SelectItem value="professional" className="text-xs">{t("comments.tonePro")}</SelectItem>
                          <SelectItem value="humorous" className="text-xs">{t("comments.toneHumor")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" data-testid="reply-comment-btn" onClick={() => handleReply(selectedComment.id)}
                        disabled={replyingId === selectedComment.id} className="h-6 text-[10px] gap-1 rounded-md">
                        {replyingId === selectedComment.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" strokeWidth={1.5} />}
                        {t("comments.generateReply")}
                      </Button>
                    </div>
                  </div>
                  {selectedComment.reply && (
                    <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                      <p className="text-xs text-foreground leading-relaxed">{selectedComment.reply}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
